import { useState, useEffect, useRef } from 'react'
import { CameraDevice } from '@renderer/types/utils'
import cogoToast from '@successtar/cogo-toast'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { isInTauriShell, listPrinters, ensureCameraPermission } from '@renderer/lib/runtime'

export interface DeviceSettings {
  cameras: {
    webcams: MediaDeviceInfo[]
    usbCameras: CameraDevice[]
  }
  printers: string[]
}

interface DeviceManagerReturn {
  devices: DeviceSettings | undefined
  isPreviewAvailable: boolean
  setIsPreviewAvailable: (available: boolean) => void
  localTempCamera: string
  setLocalTempCamera: (id: string) => void
  cameraConnectionRef: React.RefObject<{
    id: string
    connected: boolean
    isPreviewAvailable: boolean
  }>
  fetchDevices: () => Promise<void>
}

export const useDeviceManager = (): DeviceManagerReturn => {
  const [devices, setDevices] = useState<DeviceSettings>()
  const [isPreviewAvailable, setIsPreviewAvailable] = useState(false)
  const [localTempCamera, setLocalTempCamera] = useState<string>('')
  const { useWebcam } = useSettingsStore()
  const cameraConnectionRef = useRef<{
    id: string
    connected: boolean
    isPreviewAvailable: boolean
  }>({ id: '', connected: false, isPreviewAvailable: false })

  const fetchDevices = async (): Promise<void> => {
    try {
      if (window.electron && typeof window.api?.getDevices === 'function') {
        const { cameras, printers } = await window.api.getDevices()
        // Only get webcams if useWebcam is true
        const webcams = useWebcam
          ? await navigator.mediaDevices
              .enumerateDevices()
              .then((devices) => devices.filter((d) => d.kind === 'videoinput'))
              .catch(() => [])
          : []

        setDevices({
          cameras: {
            webcams,
            usbCameras: cameras
          },
          printers
        })
      } else if (isInTauriShell()) {
        if (useWebcam) await ensureCameraPermission()
        const printersList = await listPrinters()
        const printers = printersList.map((p) => p.name)
        const webcams = useWebcam
          ? await navigator.mediaDevices
              .enumerateDevices()
              .then((devices) => devices.filter((d) => d.kind === 'videoinput'))
              .catch(() => [])
          : []

        setDevices({
          cameras: {
            webcams,
            usbCameras: []
          },
          printers
        })
      } else {
        // Only get webcams if useWebcam is true
        const webcams = useWebcam
          ? await navigator.mediaDevices
              .enumerateDevices()
              .then((devices) => devices.filter((d) => d.kind === 'videoinput'))
              .catch(() => [])
          : []

        setDevices({
          cameras: {
            webcams,
            usbCameras: []
          },
          printers: []
        })
      }
    } catch (err) {
      console.error('Error fetching devices:', err)
      cogoToast.error('Failed to fetch devices')
    }
  }

  useEffect(() => {
    if (!window.electron) {
      // Only request permission if useWebcam is true
      if (useWebcam) {
        navigator.mediaDevices
          .getUserMedia({
            video: {
              frameRate: { ideal: 30, max: 60 }
            }
          })
          .then(() => {
            fetchDevices()
          })
          .catch((err) => {
            console.error('Camera permission denied:', err)
            // Don't show error, just fetch devices without webcam
            fetchDevices()
          })
      } else {
        fetchDevices()
      }
    } else {
      fetchDevices()
    }
  }, [useWebcam])

  useEffect(() => {
    let removeDeviceAddedListener: (() => void) | undefined
    let removeDeviceRemovedListener: (() => void) | undefined

    if (window.electron && window.api) {
      if (typeof window.api.onUsbDeviceAdded === 'function') {
        removeDeviceAddedListener = window.api.onUsbDeviceAdded((device: USBDevice) => {
          cogoToast.info(`Device connected: ${device.productName || 'Unknown device'}`)
          fetchDevices()
        })
      }
      if (typeof window.api.onUsbDeviceRemoved === 'function') {
        removeDeviceRemovedListener = window.api.onUsbDeviceRemoved(async (device) => {
          cogoToast.warn(`Device disconnected: ${device.productName || 'Unknown device'}`)
          fetchDevices()
        })
      }
    }

    return (): void => {
      removeDeviceAddedListener?.()
      removeDeviceRemovedListener?.()
    }
  }, [])

  return {
    devices,
    isPreviewAvailable,
    setIsPreviewAvailable,
    localTempCamera,
    setLocalTempCamera,
    cameraConnectionRef,
    fetchDevices
  }
}
